import { uploadPublicFile, edgeFunctionUrl } from './supabaseClient';
import { loadConfig } from './configService';
import { featureEnabled } from './venueSettingsService';
import { logEvent } from './debugLog';
import { t } from '../i18n';

/**
 * Visitor capture kit (feature-gated: 'capture'): photos + duration-limited
 * videos taken from the AR canvas, uploaded to the public `captures` bucket
 * (captures/<mapCode>/<session>/…), compiled into a PDF (jsPDF via cdnjs)
 * and emailed by the send-capture-email Edge Function (Resend; 501 until
 * the RESEND_API_KEY secret is set).
 *
 * NOTE: on the iOS camera path the canvas contains camera + AR (8th Wall
 * composites both) so captures look like the screen. On Android WebXR the
 * camera feed is system-composited and NOT in the canvas — captures there
 * show AR content on transparent/black. iPhone is the primary device.
 */

export interface CaptureItem {
  type: 'photo' | 'video';
  url: string;
  at: number;
}

/** Visitor-entered context for a capture, uploaded as a small JSON next to
 *  the photo/video in the same session folder; folded into the PDF report. */
export interface CaptureNote {
  poiName: string;
  /** 1–5 stars ("Rate this spot"). 0/undefined = not rated. */
  rating?: number;
  notes?: string;
  photoUrl?: string;
  videoUrl?: string;
  at: number;
}

const session: CaptureItem[] = [];
const sessionNotes: CaptureNote[] = [];
const sessionId = Math.random().toString(36).slice(2, 10);

export function captureCount(): number {
  return session.length;
}

function pathFor(ext: string): string {
  const code = loadConfig().code || 'unknown';
  return `${code}/${sessionId}/${Date.now()}.${ext}`;
}

/** Snapshot the AR canvas → JPEG → upload. */
export async function capturePhoto(canvas: HTMLCanvasElement): Promise<CaptureItem> {
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.85));
  if (!blob) throw new Error('canvas capture failed');
  const url = await uploadPublicFile('captures', pathFor('jpg'), new File([blob], 'photo.jpg', { type: 'image/jpeg' }));
  const item: CaptureItem = { type: 'photo', url, at: Date.now() };
  session.push(item);
  logEvent('session', `photo captured (${session.length} total)`);
  return item;
}

/** Record the AR canvas for `seconds` (MediaRecorder) → upload. */
export function recordVideo(
  canvas: HTMLCanvasElement,
  seconds: number,
  onTick: (remaining: number) => void,
): Promise<CaptureItem> {
  return new Promise((resolve, reject) => {
    const stream = canvas.captureStream(30);
    const mime = MediaRecorder.isTypeSupported('video/mp4') ? 'video/mp4' : 'video/webm';
    const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 2_500_000 });
    const chunks: Blob[] = [];
    rec.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
    rec.onerror = () => reject(new Error('recording failed'));
    rec.onstop = () => {
      const ext = mime === 'video/mp4' ? 'mp4' : 'webm';
      const blob = new Blob(chunks, { type: mime });
      uploadPublicFile('captures', pathFor(ext), new File([blob], `clip.${ext}`, { type: mime }))
        .then((url) => {
          const item: CaptureItem = { type: 'video', url, at: Date.now() };
          session.push(item);
          logEvent('session', `video captured ${seconds}s (${session.length} total)`);
          resolve(item);
        })
        .catch((err: unknown) => reject(err instanceof Error ? err : new Error(String(err))));
    };
    rec.start();
    let remaining = seconds;
    onTick(remaining);
    const iv = window.setInterval(() => {
      remaining--;
      onTick(remaining);
      if (remaining <= 0) {
        window.clearInterval(iv);
        rec.stop();
        stream.getTracks().forEach((t) => t.stop());
      }
    }, 1000);
  });
}

// jsPDF loaded on demand from cdnjs (kept out of the main bundle).
interface JsPdfDoc {
  addImage(data: string, fmt: string, x: number, y: number, w: number, h: number): void;
  addPage(): void;
  text(t: string, x: number, y: number): void;
  setFontSize(n: number): void;
  textWithLink(t: string, x: number, y: number, opts: { url: string }): void;
  output(type: 'blob'): Blob;
}
declare global {
  interface Window {
    jspdf?: { jsPDF: new (opts?: Record<string, unknown>) => JsPdfDoc };
  }
}
function loadJsPdf(): Promise<NonNullable<Window['jspdf']>> {
  if (window.jspdf) return Promise.resolve(window.jspdf);
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    s.onload = () => (window.jspdf ? resolve(window.jspdf) : reject(new Error('jsPDF failed to load')));
    s.onerror = () => reject(new Error('jsPDF failed to load (offline?)'));
    document.head.appendChild(s);
  });
}

import { pgSelect } from './supabaseClient';

let captureSecsCache: number | null = null;
/** Venue-configured auto-video duration (Dashboard → Captures card). */
async function getCaptureSecs(): Promise<number> {
  if (captureSecsCache !== null) return captureSecsCache;
  try {
    const rows = await pgSelect<{ capture_secs: number }>(
      'venue_settings',
      `select=capture_secs&map_code=eq.${encodeURIComponent(loadConfig().code)}`,
    );
    captureSecsCache = rows[0]?.capture_secs ?? 10;
  } catch {
    captureSecsCache = 10;
  }
  return captureSecsCache;
}

// ---------- Capture overlay (big countdown, "move around" guidance) ----------

let overlayEl: HTMLDivElement | null = null;
let overlayMsg: HTMLParagraphElement | null = null;
let overlayCount: HTMLDivElement | null = null;

function showCaptureOverlay(msg: string, count?: number): void {
  if (!overlayEl) {
    overlayEl = document.createElement('div');
    overlayEl.className = 'cap-overlay';
    overlayMsg = document.createElement('p');
    overlayMsg.className = 'cap-overlay-msg';
    overlayCount = document.createElement('div');
    overlayCount.className = 'cap-overlay-count';
    overlayEl.append(overlayCount, overlayMsg);
    document.body.appendChild(overlayEl);
  }
  overlayMsg!.textContent = msg;
  overlayCount!.textContent = count !== undefined && count > 0 ? String(count) : '';
  overlayCount!.classList.toggle('hidden', count === undefined || count <= 0);
  overlayEl.classList.remove('hidden');
}

function hideCaptureOverlay(): void {
  overlayEl?.classList.add('hidden');
}

export interface AutoCaptureResult {
  photo: CaptureItem;
  video: CaptureItem;
  seconds: number;
}

let autoBusy = false;
/**
 * Arrival auto-capture (feature 'capture' AND poi.capture): one photo + one
 * video of the venue-configured duration. While recording, a full-screen
 * overlay replaces the arrival message: "Recording — move around to capture
 * your surroundings" with a LARGE countdown. Returns the uploaded items so
 * the caller (main.ts) can open the review sheet (notes + rating);
 * null when a capture is already running.
 */
export async function autoCaptureAtPoi(
  canvas: HTMLCanvasElement,
  poiName: string,
): Promise<AutoCaptureResult | null> {
  if (autoBusy) return null;
  autoBusy = true;
  try {
    showCaptureOverlay(t('cap.photo'));
    const photo = await capturePhoto(canvas);
    const secs = await getCaptureSecs();
    showCaptureOverlay(`${t('cap.recording')} — ${t('cap.moveAround')}`, secs);
    const video = await recordVideo(canvas, secs, (remain) => {
      showCaptureOverlay(`${t('cap.recording')} — ${t('cap.moveAround')}`, remain);
    });
    showCaptureOverlay(t('cap.captured'));
    window.setTimeout(hideCaptureOverlay, 900);
    logEvent('session', `auto-capture done at "${poiName}" (${session.length} items this visit)`);
    return { photo, video, seconds: secs };
  } catch (err) {
    hideCaptureOverlay();
    throw err;
  } finally {
    autoBusy = false;
  }
}

/** Uploads the visitor's notes/rating JSON into the session folder and keeps
 *  it in memory for this session's auto-built PDF report. */
export async function submitCaptureNote(note: CaptureNote): Promise<void> {
  sessionNotes.push(note); // before upload — the auto-report must not miss it
  const blob = new Blob([JSON.stringify(note)], { type: 'application/json' });
  await uploadPublicFile('captures', pathFor('json'), new File([blob], 'note.json', { type: 'application/json' }));
  logEvent('session', `capture note saved for "${note.poiName}" (${note.rating ?? 0}★)`);
}

async function urlToDataUrl(url: string): Promise<string> {
  const b = await fetch(url).then((r) => r.blob());
  return new Promise<string>((res) => {
    const fr = new FileReader();
    fr.onload = () => res(String(fr.result));
    fr.readAsDataURL(b);
  });
}

/** Compile captures into a PDF, upload it, email links via the Edge Fn.
 *  Dashboard-only entry point; `items` defaults to this session's captures.
 *  `to` accepts one address or a list (Edge Fn validates, max 5).
 *  opts.notes: visitor notes/ratings folded in — a photo/video referenced by
 *  a note renders on that note's page (name, stars, notes text) instead of a
 *  bare page. opts.pdfPath: bucket path for the PDF (e.g. the session's own
 *  folder so the Dashboard lists it as "report.pdf"). */
export async function finishAndEmail(
  venueName: string,
  to: string | string[],
  itemsIn?: CaptureItem[],
  opts?: { notes?: CaptureNote[]; story?: string; pdfPath?: string },
): Promise<string> {
  const items = itemsIn ?? session;
  const notes = opts?.notes ?? [];
  if (items.length === 0) throw new Error('No captures yet.');
  const { jsPDF } = await loadJsPdf();
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  doc.setFontSize(18);
  doc.text(`NavMe — ${venueName}`, 15, 20);
  doc.setFontSize(11);
  doc.text(new Date().toLocaleString(), 15, 28);

  // 🤖 Visit story (agent 4) — narrative paragraph on the cover page.
  if (opts?.story) {
    doc.setFontSize(12);
    doc.text('Visit story', 15, 40);
    doc.setFontSize(10);
    let y = 47;
    let line = '';
    for (const w of opts.story.split(/\s+/)) {
      if (line && (line + ' ' + w).length > 95) {
        doc.text(line, 15, y);
        y += 5;
        line = w;
      } else line = line ? `${line} ${w}` : w;
    }
    if (line) doc.text(line, 15, y);
  }

  // Visit pages: one per note (photo + rating + notes + video link)…
  const covered = new Set<string>();
  for (const n of notes) {
    doc.addPage();
    doc.setFontSize(16);
    doc.text(n.poiName || 'Visit', 15, 20);
    doc.setFontSize(10);
    doc.text(new Date(n.at).toLocaleString(), 15, 27);
    if (n.rating && n.rating > 0) {
      doc.setFontSize(12);
      doc.text(`Rating: ${'★'.repeat(Math.min(5, n.rating))}${'☆'.repeat(Math.max(0, 5 - n.rating))}`, 15, 35);
    }
    let y = 42;
    if (n.photoUrl) {
      covered.add(n.photoUrl);
      try {
        const dataUrl = await urlToDataUrl(n.photoUrl);
        doc.addImage(dataUrl, 'JPEG', 15, y, 120, 0 as unknown as number);
        y += 100;
      } catch {
        /* photo fetch failed — keep the text content */
      }
    }
    if (n.videoUrl) {
      covered.add(n.videoUrl);
      doc.setFontSize(10);
      doc.textWithLink('🎥 Tap to watch the video', 15, y, { url: n.videoUrl });
      y += 8;
    }
    if (n.notes) {
      doc.setFontSize(11);
      // jsPDF's minimal type here has no wrap helper — chunk at ~90 chars.
      const lines: string[] = [];
      let line = '';
      for (const w of n.notes.split(/\s+/)) {
        if (line && (line + ' ' + w).length > 90) {
          lines.push(line);
          line = w;
        } else line = line ? `${line} ${w}` : w;
      }
      if (line) lines.push(line);
      for (let i = 0; i < lines.length; i++) {
        doc.text(i === 0 ? `Notes: ${lines[i]}` : lines[i], 15, y);
        y += 6;
      }
    }
  }

  // …then any captures the visitor didn't annotate.
  for (const item of items) {
    if (covered.has(item.url)) continue;
    if (item.type === 'photo') {
      const dataUrl = await urlToDataUrl(item.url);
      doc.addPage();
      doc.addImage(dataUrl, 'JPEG', 10, 20, 190, 0 as unknown as number);
      doc.setFontSize(9);
      doc.text(new Date(item.at).toLocaleTimeString(), 10, 15);
    } else {
      // Videos can't embed in a PDF — linked page instead.
      doc.addPage();
      doc.setFontSize(14);
      doc.text(`🎥 Video — ${new Date(item.at).toLocaleTimeString()}`, 15, 30);
      doc.setFontSize(10);
      doc.textWithLink('Tap to watch', 15, 40, { url: item.url });
    }
  }
  const pdfBlob = doc.output('blob');
  const pdfUrl = await uploadPublicFile(
    'captures',
    opts?.pdfPath ?? pathFor('pdf'),
    new File([pdfBlob], 'visit.pdf', { type: 'application/pdf' }),
  );
  const res = await fetch(edgeFunctionUrl('send-capture-email'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to, venueName, pdfUrl, items }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `email failed (HTTP ${res.status})`);
  }
  logEvent(
    'session',
    `capture report emailed to ${Array.isArray(to) ? to.join(', ') : to} (${items.length} items)`,
  );
  return pdfUrl;
}

// ---------- Auto-report (2026-07-12) ----------
// The Dashboard's Captures UI was removed (user spec) — the VISITOR flow now
// builds + emails the PDF report itself. Debounced after the last capture /
// review-close so a multi-stop tour coalesces into one email instead of one
// per POI. Recipients = the venue's saved report emails (set under any POI's
// 📸 checkbox); nothing configured → skip quietly (logged).

const AUTO_REPORT_DEBOUNCE_MS = 45_000;
let reportTimer: number | null = null;

export function scheduleAutoReport(): void {
  if (reportTimer !== null) window.clearTimeout(reportTimer);
  reportTimer = window.setTimeout(() => {
    reportTimer = null;
    void buildAndEmailSessionReport().catch((err: unknown) =>
      logEvent('error', `auto report failed: ${err instanceof Error ? err.message : String(err)}`),
    );
  }, AUTO_REPORT_DEBOUNCE_MS);
}

async function buildAndEmailSessionReport(): Promise<void> {
  if (session.length === 0) return;
  const code = loadConfig().code;
  const rows = await pgSelect<{ capture_emails: string[] | null }>(
    'venue_settings',
    `select=capture_emails&map_code=eq.${encodeURIComponent(code)}`,
  ).catch(() => []);
  const to = (rows[0]?.capture_emails ?? []).filter((e) => /\S+@\S+\.\S+/.test(e)).slice(0, 5);
  if (to.length === 0) {
    logEvent('session', 'auto report skipped — no report emails saved (set them under a POI’s \u{1F4F8} checkbox)');
    return;
  }
  // 🤖 Agent 4 (visit story): best-effort narrative for the cover page.
  let story: string | undefined;
  if (featureEnabled('visitStory')) {
    try {
      const photoUrls = session.filter((i) => i.type === 'photo').slice(0, 4).map((i) => i.url);
      const sr = await fetch(edgeFunctionUrl('visit-story'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          photoUrls,
          notes: sessionNotes.map((n) => ({ poiName: n.poiName, rating: n.rating, notes: n.notes })),
        }),
      });
      if (sr.ok) story = ((await sr.json()) as { story?: string }).story;
    } catch {
      /* no key / offline — the report just has no story page */
    }
  }
  // Unique name per build (a later capture re-fires this) — anon visitors
  // can INSERT into the bucket but not UPDATE, so never overwrite.
  const pdfUrl = await finishAndEmail(code, to, undefined, {
    notes: sessionNotes,
    story,
    pdfPath: `${code}/${sessionId}/report-${Date.now()}.pdf`,
  });
  logEvent('session', `auto report emailed to ${to.join(', ')} — ${pdfUrl}`);
}
